import { ReactNode, useCallback } from 'react';
import { TFunction } from 'i18next';

import { ALL_WORD, ALL } from 'global/constants';
import { DropdownListItem } from 'webclient.constants';
import { pushDataTrackingFilterEvent } from 'approot/shared/data-tracker/data-tracker-push-filters';
import {
  getSelectedOptions,
  formatDropdownLabel,
} from './dropdown-multiselect.utils';
import { DataEventProp } from 'approot/shared/data-tracker/data-tracker.constants';
import { DropdownItemCheckbox } from './dropdown-item';
import { Dropdown } from './dropdown';
import {
  DropdownSelectItem,
  DropdownSelectItemClear,
} from './dropdown-item.style';
import { MarginProps } from 'approot/shared/styles/spacing.style';

type Props = {
  options: DropdownListItem[];
  label: string;
  labelUnselected?: string;
  dataSelector: string;
  dataEvent?: DataEventProp;
  handleChangeCheckbox?: Function;
  selectedDropdownOptions?: string[];
  placeholder?: string | undefined;
  isLoading?: boolean;
  id?: string;
  displayLabel?: string;
  menuWidth?: number;
  menuMatchToggleWidth?: boolean;
  tFunction?: TFunction;
  isInline?: boolean;
  buttonElement?: ReactNode;
  hasError?: boolean;
  disabled?: boolean;
} & MarginProps;

const DropdownMultiselect = ({
  options,
  dataSelector,
  dataEvent,
  placeholder,
  label,
  labelUnselected,
  selectedDropdownOptions: selected,
  handleChangeCheckbox,
  isLoading = false,
  displayLabel,
  menuWidth,
  menuMatchToggleWidth = true,
  isInline,
  tFunction,
  buttonElement,
  hasError,
  disabled,
  ...rest
}: Props) => {
  const dropdownOptions = options.slice();
  const allIndex = dropdownOptions.findIndex(
    o => o.value === ALL || o.value === ALL_WORD
  );
  let allOptionItem: DropdownListItem | undefined = undefined;
  if (allIndex !== -1) {
    allOptionItem = dropdownOptions.splice(allIndex, 1)[0];
  }

  const selectedDropdownOptions = getSelectedOptions(selected, allOptionItem);

  const displayText = displayLabel
    ? displayLabel
    : formatDropdownLabel({
        options,
        selected: selectedDropdownOptions,
        label: label || '',
        labelUnselected,
        allOptionItem,
        t: tFunction,
      });

  const category = dataEvent?.category;

  const handleSelect = useCallback(
    (value?: string, label?: string) => {
      handleChangeCheckbox && handleChangeCheckbox(value, label);

      if (category && value && dataEvent.target) {
        pushDataTrackingFilterEvent({
          eventCategory: category,
          eventData: dataEvent.target,
          option: value,
          selectedOptions: selectedDropdownOptions,
        });
      }
    },
    [handleChangeCheckbox, category, dataEvent, selectedDropdownOptions]
  );

  return (
    <Dropdown
      menuMatchToggleWidth={menuMatchToggleWidth}
      closeMenuOnSelect={false}
      isLoading={isLoading}
      disabled={disabled}
      hasError={hasError}
      data-selector={dataSelector}
      menuWidth={menuWidth}
      buttonElement={buttonElement || displayText}
      isInline={isInline}
      dropdownContents={
        <>
          {dropdownOptions.map((option, index) => (
            <DropdownItemCheckbox
              key={`option-${index}`}
              data-selector={`${dataSelector}-option`}
              option={option}
              checked={!!selectedDropdownOptions?.includes(option.value)}
              handleSelect={() => handleSelect(option.value, option.label)}
            />
          ))}
          {allOptionItem && (
            <DropdownSelectItem
              data-selector={`${dataSelector}-option`}
              data-dropdown-item="true"
              onClick={() =>
                handleSelect(allOptionItem?.value, allOptionItem?.label)
              }
            >
              <DropdownSelectItemClear data-selector="clear-all-filter">
                Clear All
              </DropdownSelectItemClear>
            </DropdownSelectItem>
          )}
        </>
      }
      {...rest}
    />
  );
};

export default DropdownMultiselect;
